import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, GitBranch } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { branchApi } from '@/api/branchApi'
import { friendlyMessage } from '@/api/http'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { BranchGraph } from '@/components/branches/BranchGraph'

export default function BranchGraphPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const client = useQueryClient()
  const [name, setName] = useState('')
  const [fromChapter, setFromChapter] = useState(1)
  const branches = useQuery({
    queryKey: ['branches', id],
    queryFn: () => branchApi.list(id!),
    enabled: Boolean(id),
  })
  const fork = useMutation({
    mutationFn: () => branchApi.fork({
      project_id: id!,
      from_branch_id: branches.data?.find((branch) => branch.is_active)?.id ?? 'main',
      name: name.trim(),
      from_chapter: fromChapter,
    }),
    onSuccess: async () => {
      setName('')
      await client.invalidateQueries({ queryKey: ['branches', id] })
      toast.success(t('branch.forked'))
    },
    onError: (error) => toast.error(friendlyMessage(error, t('branch.forkFailed'))),
  })
  const canFork = Boolean(id && name.trim()) && Number.isInteger(fromChapter) && fromChapter > 0
    && !fork.isPending && !branches.isLoading && !branches.error

  return (
    <section className="px-4 py-6 sm:px-6 lg:px-8">
      <div className="mb-5">
        <Button asChild variant="ghost" size="sm" className="mb-3 -ml-2">
          <Link to={id ? `/projects/${id}` : '/projects'}>
            <ArrowLeft className="size-4" />
            {t('project.backToProjects')}
          </Link>
        </Button>
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-normal sm:text-3xl">
          <GitBranch className="size-6 text-primary" />
          {t('branch.title')}
        </h1>
        <p className="mt-2 max-w-3xl text-sm text-muted-foreground">{t('branch.subtitle')}</p>
      </div>
      {id ? (
        <form
          className="mb-5 flex flex-wrap items-end gap-3"
          onSubmit={(event) => {
            event.preventDefault()
            if (canFork) fork.mutate()
          }}
        >
          <div className="space-y-2">
            <Label htmlFor="branch-name">{t('branch.newName')}</Label>
            <Input id="branch-name" required value={name} onChange={(event) => setName(event.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="branch-chapter">{t('branch.forkChapter')}</Label>
            <Input id="branch-chapter" type="number" min={1} required value={fromChapter} onChange={(event) => setFromChapter(Number(event.target.value))} />
          </div>
          <Button type="submit" disabled={!canFork}>{t('branch.fork')}</Button>
        </form>
      ) : null}
      {id ? <BranchGraph projectId={id} /> : null}
    </section>
  )
}
